'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { ROOMS_MIN, ROOMS_MAX } from '@/lib/constants'
import { useI18n } from '@/lib/i18n'

const ROOM_VALUES = [1, 2, 3, 4, 5, 6] as const
const ROOM_LABELS: Record<number, string> = { 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6+' }

interface RoomButtonGroupProps {
  value: [number, number]
  onChange: (value: [number, number]) => void
  variant: 'inline' | 'compact'
  className?: string
}

export function RoomButtonGroup({ value, onChange, variant, className }: RoomButtonGroupProps) {
  const { t } = useI18n()
  const [min, max] = value
  const hasFilter = min !== ROOMS_MIN || max !== ROOMS_MAX
  const isSingle = hasFilter && min === max
  const isRange = hasFilter && min !== max

  const handleClick = (n: number) => {
    if (!hasFilter) {
      // No filter active → select single
      onChange([n, n])
    } else if (n >= min && n <= max) {
      // Clicked inside selection
      if (min === max) {
        // Single value → deselect
        onChange([ROOMS_MIN, ROOMS_MAX])
      } else if (n === min) {
        // Shrink from low end
        onChange([min + 1, max])
      } else if (n === max) {
        // Shrink from high end
        onChange([min, max - 1])
      } else {
        // Clicked middle of range → deselect
        onChange([ROOMS_MIN, ROOMS_MAX])
      }
    } else {
      // Clicked outside selection → extend range to include it
      onChange([Math.min(min, n), Math.max(max, n)])
    }
  }

  const isSelected = (n: number) => {
    if (!hasFilter) return false
    return n >= min && n <= max
  }

  const pillLabel = React.useMemo(() => {
    if (!hasFilter) return t('search.rooms', 'Rooms')
    if (isSingle) return `${t('search.rooms', 'Rooms')}: ${ROOM_LABELS[min]}`
    return `${t('search.rooms', 'Rooms')}: ${ROOM_LABELS[min]}–${ROOM_LABELS[max]}`
  }, [hasFilter, isSingle, min, max, t])

  const buttons = (
    <div className="flex items-center gap-1.5">
      {ROOM_VALUES.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => handleClick(n)}
          className={cn(
            'w-9 h-9 rounded-lg text-sm font-medium transition-all',
            'border-2 flex items-center justify-center',
            isSelected(n)
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-card text-foreground border-border hover:border-primary/50'
          )}
        >
          {ROOM_LABELS[n]}
        </button>
      ))}
    </div>
  )

  if (variant === 'inline') {
    return <div className={className}>{buttons}</div>
  }

  // compact variant: pill trigger + dropdown
  return <CompactRoomPill
    pillLabel={pillLabel}
    hasFilter={hasFilter}
    buttons={buttons}
    className={className}
  />
}

function CompactRoomPill({
  pillLabel,
  hasFilter,
  buttons,
  className,
}: {
  pillLabel: string
  hasFilter: boolean
  buttons: React.ReactNode
  className?: string
}) {
  const [isOpen, setIsOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center justify-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium',
          'border-2 transition-all whitespace-nowrap min-w-[7.5rem]',
          hasFilter
            ? 'bg-primary/10 text-primary border-primary'
            : 'bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
        )}
      >
        {pillLabel}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 z-50 p-3 rounded-xl bg-popover border border-border shadow-lg">
          {buttons}
        </div>
      )}
    </div>
  )
}
